import { ReactNode, useEffect } from "react";

import { Formik, FormikErrors, useFormikContext } from "formik";
import { ScrollView, View } from "react-native";
import { Button, Text } from "react-native-paper";
import { useTailwind } from "tailwind-rn";

import SummaryMap from "./SummaryMap";
import {
  FloatingButton,
  Form,
  Input,
  KeyboardAwareView,
  CountryPicker,
} from "../../../../components";
import useDeliveryFulfillments from "../../contexts/DeliveryFulfillmentsContext";
import {
  DeliveryFulfillmentsScreenProps,
  deliveryFulfillmentsRoutes,
} from "../../navigation/types";
import { LatLng, MapData, PickupAddress } from "../../types";

type Props = DeliveryFulfillmentsScreenProps<
  typeof deliveryFulfillmentsRoutes.pickupLocation
>;
export default function PickupLocationScreen({ navigation }: Props) {
  const { pickupAddress, savePickupAddress, selectedMapData } =
    useDeliveryFulfillments();

  const initialValues = getInitialValues(pickupAddress);

  async function onSubmit({ geolocation, ...rest }: FormValues) {
    if (!geolocation) return;
    await savePickupAddress({ ...rest, geolocation });
    navigation.goBack();
  }

  async function onValidate(values: FormValues) {
    const errors: FormikErrors<FormValues> = {};

    if (!values.address) {
      errors.address = "Los puntos de referencia son requeridos";
    }

    if (!values.contact_name) {
      errors.contact_name = "El nombre es requerido";
    }

    if (!values.phone) {
      errors.phone = "El número de WhatsApp es requerido";
    }

    return errors;
  }

  function navigateToMap() {
    navigation.navigate(deliveryFulfillmentsRoutes.map);
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={onValidate}
    >
      <PickupLocationForm
        selectedMapData={selectedMapData}
        navigateToMap={navigateToMap}
      />
    </Formik>
  );
}

export type FormValues = {
  contact_name: string;
  address: string;
  country_code: string | null;
  phone: string;
  additional_instructions: string;
  reverse_geolocation_name: string | null;
  geolocation: LatLng | null;
};

function getInitialValues(address: PickupAddress | null): FormValues {
  if (!address)
    return {
      contact_name: "",
      address: "",
      phone: "",
      country_code: null,
      additional_instructions: "",
      reverse_geolocation_name: null,
      // geolocation: null,
      geolocation: {
        latitude: 8.9824,
        longitude: -79.5199,
      },
    };

  return address;
}

function useForm() {
  return useFormikContext<FormValues>();
}

type FormProps = {
  summaryMap?: ReactNode;
  countrySelect?: ReactNode;
  contactNamePlaceholder?: string;
  addressFieldLabel?: string;
  addressFieldPlaceholder?: string;
  hideEditButton?: boolean;
  selectedMapData: MapData | null;
  navigateToMap: () => void;
};
export function PickupLocationForm({
  summaryMap,
  countrySelect,
  contactNamePlaceholder = "¿Quién entrega la orden?",
  addressFieldLabel = "Puntos de referencia",
  addressFieldPlaceholder = "e.g.: Calle 50 frente al parque Harry Strunz. A un costado de la farmacia.",
  navigateToMap,
  selectedMapData,
  hideEditButton,
}: FormProps) {
  const tailwind = useTailwind();

  const { values, errors, touched, handleSubmit, isSubmitting, setFieldValue } =
    useForm();

  function errorMessage(field: keyof FormValues) {
    return errors[field] && touched[field] ? errors[field] : undefined;
  }

  useEffect(() => {
    if (selectedMapData) {
      setFieldValue("geolocation", selectedMapData.position);
      setFieldValue("reverse_geolocation_name", selectedMapData.text);
    }
  }, [selectedMapData]);

  return (
    <KeyboardAwareView>
      <ScrollView bounces={false}>
        <View style={tailwind("m-4 gap-4 pb-24")}>
          <Text variant="titleMedium">Dirección</Text>

          <View style={tailwind("gap-1")}>
            {summaryMap || (
              <SummaryMap
                onPress={navigateToMap}
                position={values.geolocation}
              />
            )}

            <View style={tailwind("flex-row items-center")}>
              <View style={tailwind("flex-1")}>
                {values.reverse_geolocation_name && (
                  <Text>{values.reverse_geolocation_name}</Text>
                )}
              </View>

              {!hideEditButton && (
                <Button mode="text" onPress={navigateToMap}>
                  Editar
                </Button>
              )}
            </View>
          </View>

          <Form.Field
            label={addressFieldLabel}
            required
            info="Utiliza puntos de referencia para facilitar la entrega"
            error={errorMessage("address")}
          >
            <Input.Formik.Text
              fieldName="address"
              placeholder={addressFieldPlaceholder}
              multiline
              returnKeyType="default"
            />
          </Form.Field>

          <Form.Field
            label="Nombre"
            required
            error={errorMessage("contact_name")}
          >
            <Input.Formik.Text
              fieldName="contact_name"
              placeholder={contactNamePlaceholder}
            />
          </Form.Field>

          <Form.Field
            label="Whatsapp"
            icon="whatsapp"
            required
            error={errorMessage("phone")}
            info="Ingresa un número válido de WhatsApp."
          >
            <View style={tailwind("flex-row gap-2")}>
              {countrySelect}

              {/* <CountryPicker
                onCountryChange={(country) =>
                  setFieldValue("country_code", country?.value || null)
                }
              /> */}

              <Input.Formik.Text
                fieldName="phone"
                placeholder="Número de WhatsApp"
                style={tailwind("flex-1")}
              />
            </View>
          </Form.Field>

          <Form.Field label="Instrucciones adicionales (opcional)">
            <Input.Formik.Text
              fieldName="additional_instructions"
              placeholder="e.g.: tocar timbre en puerta azul"
            />
          </Form.Field>
        </View>
      </ScrollView>
      <FloatingButton
        text="Guardar"
        mode="contained"
        onPress={handleSubmit}
        disabled={isSubmitting}
        loading={isSubmitting}
      />
    </KeyboardAwareView>
  );
}
